import { AxiosError } from "axios";
import telemetry from "../../types/telemProto";

export type Sim = {
  ip: string;
  available: boolean;
  active: boolean;
  status: string;
  latency?: number;
};

export type Pilot = {
  connected: boolean;
  location: {
    latitude: number;
    longitude: number;
  };
};
export type Region = {
  geoData: any;
  id: number;
  regionName: string;
};

export interface UavNotification {
  action: string[];
  msg: string;
  type: string;
  level: 1 | 2 | 3 | 4 | 5;
}
export interface UavNotificationStamped extends UavNotification {
  timestamp: number;
  info?: boolean;
}

export interface TelemetryWithNotification {
  rosmsg: string[];
  notification?: {
    action: string[];
    msg: string;
    type: string;
    level: 1 | 2 | 3 | 4 | 5;
  };
}

export type Telemetry = {
  timestamp: number;
  source: "uav-logs";
  message: telemetry.domain.ITelem & TelemetryWithNotification;
  level: string;
  "uav-id": string;
  "phone-id": string;
  phone: {
    "cell-carrier": "";
    "cell-strength": 0;
    "cell-class": "";
    alt: 0.0;
    lat: 0.0;
    lon: 0.0;
    compass_heading: 0.0;
  };
  order: {};
  "build-hash": string;
  "uav-name": string;
  "arrived-phone": number;
  "sent-kinesis": number;
  "build-variant": string;
  "mission-id": string;
  "api-version": string;
  "phone-flight-type": string;
  category: string;
  "app-version": number;
  index: number;
  "simple-log-id": number;
};

export type CriticalTelemetry = {
  lat: number;
  lon: number;
  heading: number;
  alt: number;
  alt_lidar: number;
  battery_voltage: number;
  uav_id: string;
  flight_mode: string;
  uav_time: number;
  uav_state: string;
  uav_sub_state: string;
  notification?: UavNotification;
  mission_id: string;
  fcu_in_control: 1 | 2 | 3;
  fcu:
    | telemetry.domain.Telem.IFcu1
    | telemetry.domain.Telem.IFcu2
    | telemetry.domain.Telem.IFcu3;
};

export type DirectTelemetry = {
  id: string;
  type: string;
  pilot_info: {};
  timestamp: number;
  payload:
    | (telemetry.domain.ITelem & TelemetryWithNotification)
    | CriticalTelemetry;
};

export const isCriticalTelemetry = (
  payload: any
): payload is CriticalTelemetry => {
  if (!payload) return false;
  if (!payload?.fcu3) return true;
  return false;
};
export const isVerboseTelemetry = (
  payload: any
): payload is telemetry.domain.ITelem => {
  if (!payload) return false;
  if (payload?.fcu3) return true;
  return false;
};

export const isTelemetry = (data: any): data is Telemetry => {
  return data?.type === "TELEMETRY" && !data?.payload;
};
export const isDirectTelemetry = (data: any): data is DirectTelemetry => {
  return data?.type === "TELEMETRY" && data?.payload;
};

export type IAircraft = {
  uavId: string;
  pos: {
    lat: number;
    sats?: number;
    status?: number;
    lon: number;
    hdop?: number;
    hor_vel?: number;
    vert_vel?: number;
    health?: boolean;
  };
  compass: number;
  flightMode: string;
  uavState: string;
  uavSubState: string;
  departureTime: number;
  pilotInfo: {
    p1: {
      connected: boolean;
      location: { latitude: number; longitude: number };
    };
    p2: {
      connected: boolean;
      location: { latitude: number; longitude: number };
    };
  };
  stale: boolean;
  sims: Sim[];
  lastTelemetry: number;
  route: [number, number][];
  currentPosition: [number, number];
  mission: {
    missionId: string;
    geoJson: {
      features: { geometry: { coordinates: [number, number] } }[];
    };
    status: string;
  };
  location: {
    name: string;
    coords: { latitude: number; longitude: number };
    distance: number;
  };
  sitl: boolean;
  batteryVolts: number;
  distToHome: number;
  alt: {
    relitive_alt: number;
    lidar: number;
    amsl: number;
    wgs_84: number;
  };
  buildHash: string;
  fcuHealth: [
    { critHealth: false },
    { critHealth: true },
    { critHealth: true }
  ];
  missionPercent: number;
  uavTime: number;
};

export type VitalTelemetry = {
  flightMode?: string | null;
  heading?: number | null;
  altitude?: number | null;
  lat?: number | null;
  lon?: number | null;
  uavState?: string | null;
  uavSubState?: string | null;
  missionId?: string | null;
};

export type Role = "observer" | "p1";

export type AircraftEvent =
  | "MISSION_STOPPED"
  | "MISSION_RESET"
  | "MISSION_STARTED"
  | "MISSION_TERMINATED"
  | "MISSION_IN_FLIGHT"
  | "DOORS_OPEN"
  | "DELIVERY_COMMAND"
  | "DELIVERY_COMMAND_ENDED"
  | "AT_DROP"
  | "DOORS_CLOSED"
  | "DELIVERY_STARTED"
  | "MISSION_RECEIVED"
  | "VALIDATION_FAILED";

export interface Token {
  iss: string;
  role: string[];
  sub: string;
  exp: number;
}
export interface Route {
  id: string;
  orderId: string;
  address: string;
  eircode: string;
  enabled: boolean;
  zone: string;
  mode: null;
  devRouteName: null;
  orderCount: number;
  cellId: number;
  dropLocation: null;
  idString: string;
  address_latitude: number;
  address_longitude: number;
  misc2: null;
  misc1: null;
  km_distance: number;
}
export interface Mission {
  id: string;
  createDate: number;
  modifyDate: number;
  status: string;
  geoJson: {
    features: { geometry: { coordinates: [number, number] } }[];
  };

  avionicsApi: string;
  landingPad: {};
  missionSpeed: number;
  geoJsonLineString: string;
  geoJsonGeotunnel: string;
  geotunnelAsGeohashes: string;
  originOrderId: string;
  missionHandler: string;
  flightData: {}[];
  secureAvionicsApi: string;
}
export interface Pad {
  id: number;
  createDate: number;
  modifyDate: number;
  name: string;
  active: boolean;
  cellId: number;
  latitude: number;
  longitude: number;
  heading: number;
}
export type Locations = "none" | "moorock" | "balbriggan" | "kerry";
export type NotificationTypes = "success" | "info" | "warning" | "error";
export type SnackData = { type: NotificationTypes; message: string };
export const errorHasMessage = (err: any): err is { message: string } => {
  return !!err?.message;
};

export type MannaError = {};

export const errorHasMannaMessage = (
  err: any
): err is AxiosError<{ errorCode: number; errorMessage: string }> => {
  if (err?.response?.data?.errorCode && err?.response?.data?.errorMessage)
    return true;
  return false;
};
export type FlightType =
  | "DEV_INDOOR"
  | "DEV_FLIGHT"
  | "TRAINING"
  | "PRODUCTION"
  | "SITL"
  | "VOLUME_TESTING"
  | "STAGING"
  | "MANUAL"
  | "LFRT"
  | "SFRT"
  | "PRE_PROD"
  | "NOT_SET";

export type Command =
  | "COMMAND_PAUSE"
  | "COMMAND_DELIVER"
  | "COMMAND_RESUME"
  | "COMMAND_RTL"
  | "COMMAND_SMARTRTL"
  | "COMMAND_ABORT"
  | "COMMAND_LAND"
  | "COMMAND_DROP"
  | "COMMAND_CANCEL"
  | "COMMAND_ARM"
  | "COMMAND_DISSARM"
  | "COMMAND_PARACHUTE"
  | "COMMAND_ALT_NOGPS"
  | "COMMAND_CANCEL"
  | "COMMAND_AUTO_ARM";

export type FlightStatus =
  | "maintenance"
  | "standby"
  | "ready"
  | "takeoff"
  | "flight"
  | "pendingDelivery"
  | "dropping"
  | "atDropLocation"
  | "openingDoors"
  | "climbDelivery"
  | "returning"
  | "abort"
  | "missionComplete"
  | "missionReceived";

export interface CellGeofence {
  id: string;
  createDate: string;
  modifyDate: string;
  name: string;
  geoJson: CellGeofenceGeoJson;
  rallypoints: CellGeofenceGeoJson;
  active: boolean;
  type: string | null;
}

export interface CellGeofenceGeoJson {
  type: string;
  features: CellGeofenceGeoJsonFeature[];
}

export interface MissionReport {
  missionId: string;
  username?: string;
  pilot1?: string;
  pilot2?: string;
  aircraft?: number;
  description?: string;
  valid?: boolean;
  issue?: "NO_ISSUE" | "MINOR_ISSUE" | "MAJOR_ISSUE";
  noteCategory?: string;
  cassette?: number;
  flightType?: string;
  controlMode?: string;
  gcs?: string;
}

export interface CellGeofenceGeoJsonFeature {
  type: string; // consider enum
  properties: CellGeofenceGeoJsonFeatureProperties;
  geometry: CellGeofenceGeoJsonFeatureGeometry;
}

export interface CellGeofenceGeoJsonFeatureProperties {
  stroke: string;
  "stroke-width": number;
  "stroke-opacity": number;
  fill: string;
  "fill-opacity": number;
}

export interface CellGeofenceGeoJsonFeatureGeometry {
  type: string;
  coordinates: [[[number, number]]];
}

export interface UavRoute {
  uavId: string;
  route: Route;
  mission: Mission | null;
  missionLoaded: boolean;
}

export interface Cassette {
  active: boolean;
  cassette_name: string;
  db_type: string;
  first_flight: null;
  flight_count: number;
  flight_time: number;
  id: string;
}

export interface Uav {
  id: string;
  create_date: string;
  description: string;
  modify_date: string;
  platformId: string;
  geofence_id: string;
  telem_private_url: string;
  telem_public_url: string;
  video_private_url: string;
  video_public_url: string;
  connection_settings: string;
  sim1_url: string;
  sim2_url: string;
  ping_logging: boolean;
  crossOffsetX: number;
  crossOffsetY: number;
}
